import Konva from "konva";
import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Moveable from "react-moveable";
import { useDispatch, useSelector } from "react-redux";
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import { TooltipKeepOutArea } from "./TooltipKeepOutArea";
import { TooltipMachine } from "./TooltipMachine";
import { FreeDrawMain } from "./freeDraw/components/FreeDrawMain";

import actions from "@/actions";
import storageManager from "@/lib/storageManager";
import { RootState } from "@/reducers/types";
import { Header } from "@/sx-layout/common/Header";
import { DeleteConfirmModal } from "@/sx-layout/common/Modal";
import { HelpModal } from "@/sx-layout/common/Modal/HelpModal";
import { PlotPlanDetailObject } from "@/sx-layout/components/PlotPlanDetailObject";
import { ChangeMachineUseEndHourModal } from "@/sx-layout/components/plotmap/components/ChangeMachineUseEndHourModal";
import { CopyMachineKeepOutAreaModal } from "@/sx-layout/components/plotmap/components/CopyMachineKeepOutAreaModal";
import { CustomKeepOutAreaSubMenu } from "@/sx-layout/components/plotmap/components/CustomKeepOutAreaSubMenu";
import { DeleteKeepOutAreaModal } from "@/sx-layout/components/plotmap/components/DeleteKeepOutAreaModal";
import { DeleteMachineModal } from "@/sx-layout/components/plotmap/components/DeleteMachineModal";
import { KeepOutAreaEdit } from "@/sx-layout/components/plotmap/components/KeepOutAreaEdit";
import { KeepOutAreaObject } from "@/sx-layout/components/plotmap/components/KeepOutAreaObject";
import { KeepOutAreaSubMenu } from "@/sx-layout/components/plotmap/components/KeepOutAreaSubMenu";
import { Loading } from "@/sx-layout/components/plotmap/components/Loading";
import { MachineEditor } from "@/sx-layout/components/plotmap/components/MachineEditor";
import { MachineObject } from "@/sx-layout/components/plotmap/components/MachineObject";
import { MachineSubMenu } from "@/sx-layout/components/plotmap/components/MachineSubMenu";
import { MarkEditor } from "@/sx-layout/components/plotmap/components/MarkEditor";
import { MarkObject } from "@/sx-layout/components/plotmap/components/MarkObject";
import { MarkSubMenu } from "@/sx-layout/components/plotmap/components/MarkSubMenu";
import { PlotPlanSelector } from "@/sx-layout/components/plotmap/components/PlotPlanSelector";
import { TooltipArrow } from "@/sx-layout/components/plotmap/components/TooltipArrow";
import { TooltipMark } from "@/sx-layout/components/plotmap/components/TooltipMark";
import { TooltipText } from "@/sx-layout/components/plotmap/components/TooltipText";
import { ArrowDrawStage } from "@/sx-layout/components/plotmap/components/freeDraw/components/ArrowDrawStage";
import { CopyObjectModal } from "@/sx-layout/components/plotmap/components/freeDraw/components/CopyObjectModal";
import { EditFreeDrawArrowModal } from "@/sx-layout/components/plotmap/components/freeDraw/components/EditFreeDrawArrowModal";
import { EditFreeDrawTextModal } from "@/sx-layout/components/plotmap/components/freeDraw/components/EditFreeDrawTextModal";
import { KeepOutAreaPolygonDrawStage } from "@/sx-layout/components/plotmap/components/freeDraw/components/KeepOutAreaPolygonDrawStage";
import { TextObject } from "@/sx-layout/components/plotmap/components/freeDraw/components/TextObject";
import {
  useDrawingArea,
  useDrawingArrow,
  useSelectedArrow,
  useSelectedText,
} from "@/sx-layout/components/plotmap/components/freeDraw/hooks";
import { useSelectedArea } from "@/sx-layout/components/plotmap/components/freeDraw/hooks/useSelectedArea";
import { FreeDrawArrow, FreeDrawText, Size } from "@/sx-layout/components/plotmap/components/freeDraw/models";
import { ObjectList } from "@/sx-layout/components/plotmap/components/objectList";
import { NavigationToolbar, Toolbar } from "@/sx-layout/components/plotmap/components/toolbar";
import {
  useChangeMachineUseEndHourModal,
  useCopyModal,
  useModal,
  useNavigation,
} from "@/sx-layout/components/plotmap/hooks";
import { useCopyMarkModal } from "@/sx-layout/components/plotmap/hooks/useCopyMarkModal";
import { useMachineEditorModal } from "@/sx-layout/components/plotmap/hooks/useMachineEditorModal";
import { useMarkEditorModal } from "@/sx-layout/components/plotmap/hooks/useMarkEditorModal";
import { useMoveableArea } from "@/sx-layout/components/plotmap/hooks/useMoveableArea";
import { useMoveableMachine } from "@/sx-layout/components/plotmap/hooks/useMoveableMachine";
import { useMoveableMark } from "@/sx-layout/components/plotmap/hooks/useMoveableMark";
import { useMoveableText } from "@/sx-layout/components/plotmap/hooks/useMoveableText";
import { usePlotMapMachine } from "@/sx-layout/components/plotmap/hooks/usePlotMapMachine";
import { useWipAreaPolygon } from "@/sx-layout/components/plotmap/hooks/useWipAreaPolygon";
import {
  KeepOutArea,
  KeepOutAreaPolygon,
  KeepOutAreaRectangle,
  Machine,
  MachineStatus,
} from "@/sx-layout/components/plotmap/models";
import { ASSOCIATED_COPY, MACHINE_ICON_SIZE } from "@/sx-layout/components/plotmap/util/constants";
import { getCopyMachineCoords } from "@/sx-layout/components/plotmap/util/coords";
import { formatUseHour } from "@/sx-layout/components/plotmap/util/formatUseHour";
import { TooltipPlotPlanDetail } from "@/sx-layout/components/settings/plotPlanMapping/components/TooltipPlotPlanDetail";
import { usePlotPlanMapping } from "@/sx-layout/components/settings/plotPlanMapping/hooks/usePlotPlanMapping";
import { usePlotPlans } from "@/sx-layout/components/settings/plotPlanSetting/hooks/usePlotPlans";
import { PlotPlanType } from "@/sx-layout/components/settings/plotPlanSetting/models";
import { useAuthorization, useTerm } from "@/sx-layout/hooks";
import { useLoginUser } from "@/sx-layout/hooks/useLoginUser";
import { PlotPlanMapping, usePlotPlanMappingApi } from "@/sx-layout/hooks/usePlotPlanMappingApi";
import { useUserCompanyColor } from "@/sx-layout/hooks/useUserCompanyColor";
import { Mark, MarkType, Role } from "@/sx-layout/models";

const REVIEW_MAP_HEIGHT = 214;
const REVIEW_MAP_WIDTH = 300;
const IMAGE_BASE_WIDTH = 2000;
const MAX_IMAGE_SIZE = 10000; // 最大ズーム時に画像幅を10000px相当まで拡大する
const HALF_KEEP_OUT_AREA_SIZE = 50; //新規作成時の立入禁止エリアの座標調整（100x100pxで作成するのでその半分　→　立入禁止エリアは左上が起点となるため）

Konva.pixelRatio = 5; // 自由描画の品質：大きいほど高くなる

export const PlotMap = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // ユーザー情報
  const { loginUser } = useLoginUser();
  const loginUserCompanyId = loginUser?.company_id;
  const { loginUserCompanyIconColor } = useUserCompanyColor();
  // 工事
  const constructionId = useSelector((state: RootState) => state.construction.constructionId);
  // プロットプラン
  const plotPlanId = useSelector((state: RootState) => state.plotmap.plotPlanId);
  const { plotPlanDetails, refresh: refreshPlotPlanDetails } = usePlotPlanMapping(Number(plotPlanId));
  const { plotPlans, refreshPlotPlans } = usePlotPlans();
  const plotPlan = useMemo(
    () => plotPlans.find((plan) => String(plan.plot_plan_id) == plotPlanId),
    [plotPlanId, plotPlans]
  );
  const plotPlanMappingApi = usePlotPlanMappingApi();
  const [clipAreas, setClipAreas] = useState<PlotPlanMapping[]>([]);
  const [plotPlanSelectorOptions, setPlotPlanSelectorOptions] = useState([]);
  const refreshPlotPlanSelectorOptions = async () => {
    const sortedPlotPlans = plotPlans.slice().sort((a, b) => a.plot_plan_id - b.plot_plan_id);
    if (sortedPlotPlans.length === 0) {
      setPlotPlanSelectorOptions([]);
      return;
    }
    const list = [];
    const plotPlansOverview = sortedPlotPlans.filter((v) => v.plot_plan_type === PlotPlanType.OVERVIEW);
    for (const overview of plotPlansOverview) {
      list.push({
        label: overview.plot_plan_name,
        value: overview.plot_plan_id,
      });
      const plotPlanMappingRes = await plotPlanMappingApi.fetchPlotPlanMappings({
        overview_plot_plan_id: overview.plot_plan_id,
      });
      const sortedMappings = (plotPlanMappingRes.plot_plan_mappings ?? [])
        .slice()
        .sort((a, b) => a.plot_plan_mapping_id - b.plot_plan_mapping_id);
      for (const mapping of sortedMappings) {
        const plotPlanDetail = sortedPlotPlans.find((plan) => plan.plot_plan_id === mapping.detail_plot_plan_id);
        list.push({
          label: ` └ ${plotPlanDetail.plot_plan_name}`,
          value: plotPlanDetail.plot_plan_id,
        });
      }
    }
    const mapped = list.map((v) => v.value);
    const rest = sortedPlotPlans.filter((v) => !mapped.includes(v.plot_plan_id));
    for (const plan of rest) {
      list.push({
        label: plan.plot_plan_name,
        value: plan.plot_plan_id,
      });
    }
    setPlotPlanSelectorOptions(list);
  };
  useEffect(() => {
    refreshPlotPlanSelectorOptions();
  }, [plotPlans]);

  useEffect(() => {
    if (!plotPlanId || plotPlanId === "undefined") {
      setClipAreas([]);
      return;
    }
    plotPlanMappingApi
      .fetchPlotPlanMappings({ detail_plot_plan_id: Number(plotPlanId) })
      .then((res) => {
        setClipAreas(res?.plot_plan_mappings ?? []);
      })
      .catch(() => {
        setClipAreas([]);
      });
  }, [plotPlanId]);

  // ローディング
  const fetching = useSelector<RootState, boolean>(
    (state) => state.plotPlan.fetching || state.plotmap.fetching || state.machine.fetching
  );
  const [isLoading, setIsLoading] = useState(false);
  // 配置図
  const mapImageBlob = useSelector<RootState, Blob>((state) => state.plotmap.mapImage);
  const mapImage = useMemo(() => {
    return mapImageBlob && URL.createObjectURL(mapImageBlob);
  }, [mapImageBlob]);
  useEffect(() => {
    if (!plotPlanId || (typeof plotPlanId === "string" && plotPlanId === "undefined")) {
      setIsLoading(false);
    } else {
      setIsLoading(!mapImage || fetching);
    }
  }, [fetching, mapImage, plotPlanId]);
  // 日付
  const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);

  // プロットマップオブジェクト（全データ）
  const showUserCompanyOnly = useSelector<RootState, boolean>((state) => state.plotmap.displayLoggedInCompanyOnly);
  const filterCompany = useCallback(
    (list) => {
      if (!showUserCompanyOnly || !loginUserCompanyId) return list;

      return list.filter((v) => v.company_id === loginUserCompanyId);
    },
    [showUserCompanyOnly, loginUserCompanyId]
  );
  const plotMapMachine = usePlotMapMachine();
  const allMarks = useSelector((state: RootState) => state.plotmap.marks);
  const allKeepOutAreas = useSelector<RootState, KeepOutAreaRectangle[]>((state) => state.plotmap.keepOutAreas);
  const allKeepOutAreasPolygon = useSelector<RootState, KeepOutAreaPolygon[]>(
    (state) => state.plotmap.keepOutAreasPolygon
  );
  const wipKeepOutAreaPolygon = useWipAreaPolygon();
  const allArrows = useSelector<RootState, FreeDrawArrow[]>((state) => state.plotmap.arrows);
  const allTexts = useSelector<RootState, FreeDrawText[]>((state) => state.plotmap.texts);

  // プロットマップオブジェクト（表示用）
  const machines = useMemo(() => filterCompany(plotMapMachine.machines), [plotMapMachine.machines, filterCompany]);
  const marks = useMemo(() => filterCompany(allMarks), [allMarks, filterCompany]);
  const keepOutAreas = useMemo(() => filterCompany(allKeepOutAreas), [allKeepOutAreas, filterCompany]);
  const keepOutAreasPolygon: KeepOutAreaPolygon[] = useMemo(
    () => filterCompany(allKeepOutAreasPolygon),
    [allKeepOutAreasPolygon, filterCompany]
  );
  const arrows = useMemo(() => filterCompany(allArrows), [allArrows, filterCompany]);
  const texts = useMemo(() => filterCompany(allTexts), [allTexts, filterCompany]);

  // ナビゲーション
  const navigation = useNavigation();

  // Modal
  const keepOutAreaEditorModal = useModal();
  const copyModal = useCopyModal();
  const copyMarkModal = useCopyMarkModal();
  const copyCustomAreaModal = useModal();
  const deleteCustomAreaModal = useModal();
  const deleteMachineModal = useModal();
  const deleteAreaModal = useModal();
  const deleteMarkModal = useModal();
  const changeMachineUseEndHourModal = useChangeMachineUseEndHourModal();
  const helpModal = useModal();
  const plotPlanSelector = useModal();
  const editArrowModal = useModal();
  const deleteArrowModal = useModal();
  const copyArrowModal = useModal();
  const editTextModal = useModal();
  const copyTextModal = useModal();
  const deleteTextModal = useModal();

  // プロットマップ編集
  const [selectedMachine, setSelectedMachine] = useState<Machine>(null);
  const [selectedMark, setSelectedMark] = useState<Mark>(null);
  const [selectedPlotPlanDetailId, setSelectedPlotPlanDetailId] = useState<number | null>(null);
  const [plotPlanDetailOrder, setPlotPlanDetailOrder] = useState<number[]>([]);
  const [overviewPlotPlanId, setOverviewPlotPlanId] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (plotPlan?.plot_plan_type !== PlotPlanType.DETAIL) {
      setOverviewPlotPlanId(undefined);
    }
  }, [plotPlan?.plot_plan_type]);
  const [tooltipLeftOrRight, setTooltipLeftOrRight] = useState<"left-aligned" | "right-aligned">("left-aligned");
  const selectedMachineRef = useRef<HTMLDivElement>();
  const selectedMarkRef = useRef<HTMLDivElement>();
  const machineMoveableRef = useRef<HTMLDivElement>();
  const markMoveableRef = useRef<HTMLDivElement>();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const [selectedArea, setSelectedArea] = useState<KeepOutArea>();
  const [withMachine, setWithMachine] = useState<Machine>();
  const selectedAreaRef = useRef<HTMLDivElement>();
  const areaMoveableRef = useRef<HTMLDivElement>();

  const [stageSize, setStageSize] = useState<Size>({ width: 0, height: 0 });
  const drawScreenElem = useRef<HTMLDivElement>();

  const drawingArrow = useDrawingArrow({ defaultColor: loginUserCompanyIconColor, loginUserCompanyId });
  const drawingArea = useDrawingArea();
  const selectedArrow = useSelectedArrow();
  const selectedText = useSelectedText();
  const selectedAreaPolygon = useSelectedArea();

  const selectedTextRef = useRef<HTMLDivElement>();
  const textMoveableRef = useRef<HTMLDivElement>();

  // Privileges
  const { isAdmin, isUser, isSpectator } = useAuthorization();
  const layoutRoles = useSelector<RootState, Role>((state) => state.app.roles.layout?.layout);
  useEffect(() => {
    navigation.setEditable(layoutRoles?.update);
  }, [layoutRoles]);
  const isMachineFromOtherCompany = useMemo(() => {
    return selectedMachine && loginUserCompanyId && selectedMachine.company_id !== loginUserCompanyId;
  }, [selectedMachine, loginUserCompanyId]);
  const isAreaFromOtherCompany = useMemo(() => {
    return selectedArea && loginUserCompanyId && selectedArea.company_id !== loginUserCompanyId;
  }, [selectedArea, loginUserCompanyId]);
  const isMarkFromOtherCompany = useMemo(() => {
    return selectedMark && loginUserCompanyId && selectedMark.company_id !== loginUserCompanyId;
  }, [selectedMark, loginUserCompanyId]);
  const { isPast, isToday } = useTerm(layoutDate);

  // Component refs
  const contentRef = useRef(null);
  const menuRef = useRef(null);
  const operationRef = useRef(null);
  const tableRef = useRef(null);
  const mapRef = useRef(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const imageRef = useRef(null);

  // Map control
  const [isDragging, setIsDragging] = React.useState(false);
  const resizeTarget = useRef<{ top: number; left: number; width: number; height: number }>();
  const [minScale, setMinScale] = useState(0.5);
  const [maxScale, setMaxScale] = useState(5);
  const [reviewMap, setReviewMap] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  // 横幅2000を基準にした時の各配置箇所の比率の設定(但し2000px => 200%)
  const scale = transformRef.current ? 1 / transformRef.current.state.scale : 1;
  const [mapRatio, setMapRatio] = useState(1);
  const [magnificationRatio, setMagnificationRatio] = useState(Number(storageManager.getLayoutMapMagnification()));

  const machineEditorModal = useMachineEditorModal({
    mapX: imageRef.current?.offsetWidth,
    mapY: imageRef.current?.offsetHeight,
    mapRatio,
  });
  const markEditorModal = useMarkEditorModal({
    mapX: imageRef.current?.offsetWidth,
    mapY: imageRef.current?.offsetHeight,
    mapRatio,
  });

  // 重機移動ロジック
  const moveableMachine = useMoveableMachine({
    moveable: !isPast && (isAdmin || (isUser && !isMachineFromOtherCompany)),
    selectedMachine,
    updateSelectedMachine: setSelectedMachine,
    withArea: keepOutAreas.find((a) => a.keepout_area_id === selectedMachine?.keepout_area_id),
    machineRef: selectedMachineRef,
    imageRef,
    scale,
    mapRatio,
    setIsDragging,
  });
  // テキスト移動ロジック
  const moveableText = useMoveableText({
    moveable: !isPast && selectedText.isEditable,
    selectedText: selectedText.freeDrawText,
    updateSelectedText: (text) => selectedText.changeCoord(text),
    textRef: selectedTextRef,
    imageRef,
    scale,
    mapRatio,
    setIsDragging,
  });
  // 立入禁止エリア移動ロジック
  const moveableArea = useMoveableArea({
    moveable: !isPast && (isAdmin || (isUser && !isAreaFromOtherCompany)),
    selectedArea,
    updateSelectedArea: setSelectedArea,
    withMachine: machines.find((m) => m.machine_id === selectedArea?.machine_id),
    areaRef: selectedAreaRef,
    imageRef,
    scale,
    mapRatio,
    setIsDragging,
    resizeTarget,
  });
  // マーク移動ロジック
  const moveableMark = useMoveableMark({
    moveable: !isPast && (isAdmin || (isUser && !isMarkFromOtherCompany)),
    selectedMark,
    updateSelectedMark: setSelectedMark,
    markRef: selectedMarkRef,
    imageRef,
    scale,
    mapRatio,
    setIsDragging,
  });

  const prevLayoutDateRef = useRef(layoutDate);
  useEffect(() => {
    // 配置日が変更されてなければ更新をスキップ
    if (prevLayoutDateRef.current === layoutDate || fetching) {
      return;
    }
    prevLayoutDateRef.current = layoutDate;
  }, [layoutDate, fetching]);

  // FIXME: 排他エラーを共通のエラーで処理する仕組みを変えたい
  // 排他エラーで再読み込みしたらモーダルを閉じる
  useEffect(() => {
    if (selectedMachine && (machineEditorModal.isOpen || deleteMachineModal.isOpen)) {
      const machine = machines.find((v) => v.machine_id === selectedMachine.machine_id);
      if (machine?.timestamp?.update_date !== selectedMachine.timestamp.update_date) {
        machineEditorModal.close();
        deleteMachineModal.close();
        setSelectedMachine(null);
        selectedMachineRef.current = null;
      }
    }
  }, [machines, selectedMachine, machineEditorModal.isOpen, deleteMachineModal.isOpen]);

  useEffect(() => {
    if (selectedMark && (markEditorModal.isOpen || deleteMarkModal.isOpen)) {
      const mark = marks.find((v) => v.mark_id === selectedMark.mark_id);
      if (mark?.timestamp?.update_date !== selectedMark.timestamp.update_date) {
        markEditorModal.close();
        deleteMarkModal.close();
        setSelectedMark(null);
        selectedMarkRef.current = null;
      }
    }
  }, [marks, selectedMark, markEditorModal.isOpen, deleteMarkModal.isOpen]);

  useEffect(() => {
    if (withMachine && keepOutAreaEditorModal.isOpen) {
      const machine = machines.find((v) => v.machine_id === withMachine.machine_id);
      if (machine?.timestamp?.update_date !== withMachine.timestamp.update_date) {
        keepOutAreaEditorModal.close();
        setWithMachine(null);
        setSelectedMachine(null);
        selectedMachineRef.current = null;
      }
    }
  }, [machines, withMachine, keepOutAreaEditorModal.isOpen]);

  useEffect(() => {
    if (selectedArea && (keepOutAreaEditorModal.isOpen || deleteAreaModal.isOpen)) {
      const area = keepOutAreas.find((v) => v.keepout_area_id === selectedArea.keepout_area_id);
      if (area?.timestamp?.update_date !== selectedArea.timestamp.update_date) {
        keepOutAreaEditorModal.close();
        deleteAreaModal.close();
        setSelectedArea(null);
        setWithMachine(null);
        selectedAreaRef.current = null;
      }
    }
  }, [keepOutAreas, selectedArea, keepOutAreaEditorModal.isOpen, deleteAreaModal.isOpen]);

  useEffect(() => {
    if (selectedArrow.freeDrawArrow && (editArrowModal.isOpen || deleteArrowModal.isOpen)) {
      const arrow = arrows.find((v) => v.arrow_id === selectedArrow.freeDrawArrow.arrow_id);
      if (arrow?.timestamp?.update_date !== selectedArrow.freeDrawArrow.timestamp.update_date) {
        editArrowModal.close();
        deleteArrowModal.close();
        selectedArrow.end();
      }
    }
  }, [arrows, selectedArrow.freeDrawArrow, editArrowModal.isOpen, deleteArrowModal.isOpen]);

  useEffect(() => {
    if (selectedText.freeDrawText && (editTextModal.isOpen || deleteTextModal.isOpen)) {
      const text = texts.find((v) => v.freetext_id === selectedText.freeDrawText.freetext_id);
      if (text?.timestamp?.update_date !== selectedText.freeDrawText.timestamp.update_date) {
        editTextModal.close();
        deleteTextModal.close();
        selectedText.end();
      }
    }
  }, [texts, selectedText.freeDrawText, editTextModal.isOpen, deleteTextModal.isOpen]);

  useEffect(() => {
    if (selectedAreaPolygon.freeDrawArea && (keepOutAreaEditorModal.isOpen || deleteCustomAreaModal.isOpen)) {
      const area = keepOutAreasPolygon.find(
        (v) => v.keepout_area_id === selectedAreaPolygon.freeDrawArea.keepout_area_id
      );
      if (area?.timestamp?.update_date !== selectedAreaPolygon.freeDrawArea.timestamp.update_date) {
        keepOutAreaEditorModal.close();
        deleteCustomAreaModal.close();
        selectedAreaPolygon.end();
      }
    }
  }, [
    keepOutAreasPolygon,
    selectedAreaPolygon.freeDrawArea,
    keepOutAreaEditorModal.isOpen,
    deleteCustomAreaModal.isOpen,
  ]);

  const setKonvaStageSize = useMemo(
    () =>
      _.debounce(() => {
        // Konvaの描画サイズ
        setStageSize({
          width: drawScreenElem.current?.clientWidth ?? 0,
          height: drawScreenElem.current?.clientHeight ?? 0,
        });
      }, 50),
    []
  );

  useEffect(() => {
    const handleResizeMap = () => {
      if (mapImage && imageRef.current) {
        setMapRatio(imageRef.current.offsetWidth / IMAGE_BASE_WIDTH);
        setMaxScale(MAX_IMAGE_SIZE / imageRef.current.offsetWidth);

        const magnificationRatio = (imageRef.current.offsetWidth / IMAGE_BASE_WIDTH / scale) * 100 * 2;
        setMagnificationRatio(magnificationRatio);
        storageManager.setLayoutMapMagnification(magnificationRatio);

        setKonvaStageSize();
      }
    };

    handleResizeMap();
    window.addEventListener("resize", handleResizeMap);

    return () => {
      window.removeEventListener("resize", handleResizeMap);
    };
  }, [mapImage, imageRef.current]);

  useEffect(() => {
    setMagnificationRatio((mapRatio / scale) * 2 * 100);
  }, [mapRatio, scale]);

  const updateNavigationArea = () => {
    if (transformRef.current && mapRef.current && imageRef.current) {
      const mapClientRect = mapRef.current.getBoundingClientRect();
      const imageClientRect = imageRef.current.getBoundingClientRect();
      if (mapClientRect.width !== 0 && imageClientRect.width !== 0) {
        const widthRatio = mapClientRect.width / imageClientRect.width;
        const heightRatio = REVIEW_MAP_WIDTH / mapClientRect.width;
        const reviewMapWidth = REVIEW_MAP_WIDTH * widthRatio;
        const { positionX, positionY, previousScale, scale } = transformRef.current.state;

        // NOTE: モーダルを表示している状態でリサイズした場合は選択中のオブジェクトをクリアしないこと
        if (!isAnyModalOpen) {
          setSelectedMachine(null);
          setSelectedArea(null);
          setSelectedPlotPlanDetailId(null);
          setWithMachine(null);
          setKeepOutAreaSubMenuRef(null);
          setMachineSubMenuRef(null);
          areaMoveableRef.current = null;
          machineMoveableRef.current = null;

          setSelectedMark(null);
          setMarkSubMenuRef(null);
          markMoveableRef.current = null;
        }

        // 赤枠の位置・サイズを計算し、ミニマップ画像の範囲内にクランプする
        // reviewMapWidth が REVIEW_MAP_WIDTH を超える場合（画像が横に広い）も含め共通ロジックで処理する
        const top = (positionY * -heightRatio) / scale - 2;
        const rawLeft = (positionX * -reviewMapWidth) / (imageClientRect.width * widthRatio) - 2;
        const height = mapClientRect.height * (reviewMapWidth / mapClientRect.width) + 2;

        // ミニマップ上の画像表示サイズ（赤枠がはみ出さないようにするための上限）
        const redBoxWidth = Math.min(reviewMapWidth, REVIEW_MAP_WIDTH);
        const reviewMapHeight = (REVIEW_MAP_WIDTH * imageClientRect.height) / imageClientRect.width;

        // 横方向クランプ
        const clampedLeft = Math.max(rawLeft, -2);
        const clampedRight = Math.min(clampedLeft + redBoxWidth, REVIEW_MAP_WIDTH);

        // 縦方向クランプ
        const clampedTop = Math.max(top, -2);
        const clampedBottom = Math.min(clampedTop + height, reviewMapHeight);

        setReviewMap({
          top: clampedTop,
          left: clampedLeft,
          width: Math.max(0, clampedRight - clampedLeft),
          height: Math.max(0, clampedBottom - clampedTop),
        });

        if (REVIEW_MAP_WIDTH < Math.round(reviewMapWidth) && previousScale > scale) {
          transformRef.current.centerView();
        }
      }
    }
  };

  const resizeMap = useMemo(
    () =>
      _.debounce((height: number) => {
        if (imageRef.current?.offsetHeight) {
          // 最小拡大率を変更する
          const newMinScale = Math.min(
            height / imageRef.current.offsetHeight,
            mapRef.current.clientWidth / imageRef.current.offsetWidth
          );
          setMinScale(newMinScale);
          // 最小拡大率に合わせる
          const { state, setTransform } = transformRef.current;
          if (state.scale < newMinScale) {
            setTimeout(() => {
              const wrapperWidth = mapRef.current.clientWidth;
              const wrapperHeight = height;
              const contentWidth = imageRef.current.offsetWidth;
              const contentHeight = imageRef.current.offsetHeight;

              const nextPositionX = (wrapperWidth - contentWidth * newMinScale) / 2;
              const nextPositionY = (wrapperHeight - contentHeight * newMinScale) / 2;

              setTransform(nextPositionX, nextPositionY, newMinScale, 50);

              setTimeout(() => updateNavigationArea(), 500);
            }, 200);
          } else {
            setTimeout(() => updateNavigationArea(), 500);
          }
        }
      }, 200),
    [updateNavigationArea]
  );
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (!contentRef.current || !menuRef.current || !operationRef.current) return;
      const h =
        contentRef.current.offsetHeight -
        menuRef.current.offsetHeight -
        operationRef.current.offsetHeight -
        (tableRef.current?.offsetHeight ?? 0);
      // 拡大率調整
      resizeMap(h);
    });

    resizeObserver.observe(mapRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [plotPlanId]);

  useEffect(() => {
    if (plotPlan?.map_image_id) {
      dispatch(actions.plotmap.fetchMapImage(plotPlan.map_image_id));
    }
  }, [plotPlan?.map_image_id]);

  const { construction } = useSelector((state: RootState) => state.construction);
  useEffect(() => {
    if (plotPlanId) {
      plotMapMachine.refreshMachines();
      dispatch(actions.plotmap.fetchMarks(Number(plotPlanId), layoutDate));
      dispatch(actions.plotmap.fetchKeepOutAreas(Number(plotPlanId), layoutDate));
      dispatch(actions.plotmap.fetchArrows(Number(plotPlanId), layoutDate));
      dispatch(actions.plotmap.fetchTexts(Number(plotPlanId), layoutDate));
    } else {
      if (construction?.construction_id) {
        plotPlanSelector.open();
      } else {
        plotPlanSelector.close();
      }
    }
  }, [plotPlanId, layoutDate, construction]);

  const isAnyModalOpen =
    machineEditorModal.isOpen ||
    keepOutAreaEditorModal.isOpen ||
    copyModal.target ||
    deleteMachineModal.isOpen ||
    deleteAreaModal.isOpen ||
    deleteMarkModal.isOpen ||
    changeMachineUseEndHourModal.isOpen ||
    editArrowModal.isOpen ||
    deleteArrowModal.isOpen ||
    copyArrowModal.isOpen ||
    markEditorModal.isOpen ||
    helpModal.isOpen;

  const handleClick = useCallback(
    (event) => {
      if (isAnyModalOpen) {
        return;
      }
      const paths = event.composedPath();

      // 重機ツールチップの添付ファイルのダウンロードリンクのクリック時は、プレビューとダウンロードダイアログの表示のため、制御する
      if (
        paths.some((path: HTMLElement) => path.id === "tooltip-machine") &&
        paths.some((path: HTMLElement) => path.tagName === "A")
      ) {
        return;
      }

      // 重機ツールチップの添付ファイルの画像プレビューを閉じた場合であっても、ツールチップは表示したままにする
      if (
        paths.some((path: HTMLElement) => {
          if (typeof path.className === "string") {
            return path.className.includes("react-images");
          }

          return false;
        })
      ) {
        return;
      }

      if (
        paths.every((path: HTMLElement) => {
          return String(path.id).indexOf("area-") !== 0;
        })
      ) {
        setKeepOutAreaSubMenuRef(null);
        setSelectedArea(null);
        setWithMachine(null);
        areaMoveableRef.current = null;
      }

      if (
        paths.every((path: HTMLElement) => {
          return String(path.id).indexOf("machine-") !== 0;
        })
      ) {
        setMachineSubMenuRef(null);
        setSelectedMachine(null);
        machineMoveableRef.current = null;
      }
      if (
        paths.every((path: HTMLElement) => {
          return String(path.id).indexOf("mark-") !== 0 && String(path.id) !== "copy-object-modal";
        })
      ) {
        setMarkSubMenuRef(null);
        setSelectedMark(null);
        markMoveableRef.current = null;
      }
      if (
        plotPlan?.plot_plan_type === PlotPlanType.OVERVIEW &&
        paths.every((path: HTMLElement) => {
          return (
            String(path.id).indexOf("text-") !== 0 &&
            path.id !== "text-tool" &&
            path.id !== "text-edit-modal" &&
            path.id !== "text-delete-modal" &&
            path.id !== "copy-object-modal"
          );
        })
      ) {
        selectedText.end();
        selectedTextRef.current = null;
        textMoveableRef.current = null;
      }
      if (
        paths.every((path: HTMLElement) => {
          return String(path.id).indexOf("plot-plan-detail-") !== 0;
        })
      ) {
        setSelectedPlotPlanDetailId(null);
      }
    },
    [isAnyModalOpen, selectedArea, selectedText, plotPlan?.plot_plan_type]
  );

  useEffect(() => {
    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [handleClick]);

  useEffect(() => {
    if (constructionId) {
      refreshPlotPlans();
      // localStorageから前回のPlotPlanを復元する
      const savedPlotPlanId = storageManager.getPlotPlanId(constructionId);
      if (savedPlotPlanId) {
        dispatch(actions.plotmap.selectPlotPlan(savedPlotPlanId));
      }
    }
    storageManager.setUserItem("system", "layout");
  }, [constructionId]);

  // 拡大縮小時のメニューのサイズ調整
  useEffect(() => {
    if (document.querySelector("head #rpt-container-style")) {
      document.querySelector("head #rpt-container-style").remove();
    }
    const style = `<style id="rpt-container-style"> .rpt-container { transform: scale(${scale}) }<style>`;
    document.querySelector("head").insertAdjacentHTML("beforeend", style);
  }, [scale]);

  const [initialPosition, setInitialPosition] = useState({
    machine: {
      initialX: 0,
      initialY: 0,
    },
    keepOutArea: {
      initialX: 0,
      initialY: 0,
    },
  });

  useEffect(() => {
    if (imageRef.current && mapRef.current) {
      setInitialPosition({
        machine: {
          initialX: Math.round(
            (-imageRef.current.getBoundingClientRect().x +
              mapRef.current.getBoundingClientRect().x +
              mapRef.current.getBoundingClientRect().width / 2) /
              (imageRef.current.getBoundingClientRect().width / IMAGE_BASE_WIDTH)
          ),
          initialY: Math.round(
            (-imageRef.current.getBoundingClientRect().y +
              mapRef.current.getBoundingClientRect().y +
              mapRef.current.getBoundingClientRect().height / 2) /
              (imageRef.current.getBoundingClientRect().width / IMAGE_BASE_WIDTH)
          ),
        },
        keepOutArea: {
          initialX:
            Math.round(
              (-imageRef.current.getBoundingClientRect().x +
                mapRef.current.getBoundingClientRect().x +
                mapRef.current.getBoundingClientRect().width / 2) /
                (imageRef.current.getBoundingClientRect().width / IMAGE_BASE_WIDTH)
            ) - HALF_KEEP_OUT_AREA_SIZE,
          initialY:
            Math.round(
              (-imageRef.current.getBoundingClientRect().y +
                mapRef.current.getBoundingClientRect().y +
                mapRef.current.getBoundingClientRect().height / 2) /
                (imageRef.current.getBoundingClientRect().width / IMAGE_BASE_WIDTH)
            ) - HALF_KEEP_OUT_AREA_SIZE,
        },
      });
    }
  }, [reviewMap]);

  const selectedPlotPlanDetailName = useMemo(() => {
    return plotPlans.find((v) => v.plot_plan_id === selectedPlotPlanDetailId)?.plot_plan_name ?? "";
  }, [plotPlans, selectedPlotPlanDetailId]);

  const handleImageLoaded = () => {
    if (mapRef.current && transformRef.current && imageRef.current) {
      const mapClientRect = mapRef.current.getBoundingClientRect();
      const fitHeightScale = mapClientRect.height / imageRef.current.offsetHeight;
      const fitWidthScale = mapClientRect.width / imageRef.current.offsetWidth;
      setMinScale(Math.min(fitHeightScale, fitWidthScale));

      // 横長画像（縦が余る）の場合、縦100%になるよう初期表示をズームする
      if (imageRef.current.offsetHeight < mapClientRect.height) {
        const { setTransform } = transformRef.current;
        const scaledWidth = imageRef.current.offsetWidth * fitHeightScale;
        const posX = (mapClientRect.width - scaledWidth) / 2;
        // setTimeout を排除してアニメーションなしで即座に適用することで2段階ズームを防ぐ
        setTransform(posX, 0, fitHeightScale, 0);
        setTimeout(() => updateNavigationArea(), 100);
      } else {
        updateNavigationArea();
      }
    }
  };

  const getZoomScaleFromMagnification = (magnification: number) => {
    return magnification / (mapRatio * 2 * 100);
  };

  const getNextZoomMagnification = (currentMagnification: number, direction: "in" | "out") => {
    const step = 100;
    const roundedMagnification = Math.round(currentMagnification);
    if (direction === "in") {
      return Math.ceil((roundedMagnification + 1) / step) * step;
    }
    return Math.floor((roundedMagnification - 1) / step) * step;
  };

  const handleZoomIn = () => {
    if (!transformRef.current) return;
    const { state, setTransform } = transformRef.current;

    const currentMagnification = mapRatio * state.scale * 2 * 100;
    const nextMagnification = getNextZoomMagnification(currentMagnification, "in");
    const targetScale = getZoomScaleFromMagnification(nextMagnification);
    const newScale = Math.min(maxScale, Math.max(minScale, targetScale));

    const wrapper = mapRef.current as HTMLDivElement;
    const cx = wrapper.clientWidth / 2;
    const cy = wrapper.clientHeight / 2;
    const oldScale = state.scale;
    const contentX = (cx - state.positionX) / oldScale;
    const contentY = (cy - state.positionY) / oldScale;

    const newPosX = cx - contentX * newScale;
    const newPosY = cy - contentY * newScale;

    setTransform(newPosX, newPosY, newScale, 200);

    setTimeout(() => {
      updateNavigationArea();
    }, 200);
  };

  const handleZoomOut = () => {
    if (!transformRef.current) return;
    const { state, setTransform } = transformRef.current;

    const currentMagnification = mapRatio * state.scale * 2 * 100;
    const nextMagnification = getNextZoomMagnification(currentMagnification, "out");
    const targetScale = getZoomScaleFromMagnification(nextMagnification);
    const newScale = Math.min(maxScale, Math.max(minScale, targetScale));

    const wrapper = mapRef.current as HTMLDivElement;
    const cx = wrapper.clientWidth / 2;
    const cy = wrapper.clientHeight / 2;
    const oldScale = state.scale;
    const contentX = (cx - state.positionX) / oldScale;
    const contentY = (cy - state.positionY) / oldScale;
    const newPosX = cx - contentX * newScale;
    const newPosY = cy - contentY * newScale;

    setTransform(newPosX, newPosY, newScale, 200);

    setTimeout(() => {
      updateNavigationArea();
    }, 200);
  };

  const zoomToElement = (id) => {
    if (transformRef.current) {
      const { zoomToElement, state } = transformRef.current;
      zoomToElement(id, state.scale, 300);
      setTimeout(() => {
        updateNavigationArea();
      }, 300);
    }
  };

  const zoomToMachine = (no: number) => {
    zoomToElement(`machine-${no}`);
  };
  const zoomToAreaRectangle = (id: number) => {
    zoomToElement(`area-${id}`);
  };
  const zoomToMark = (id: number) => {
    zoomToElement(`mark-${id}`);
  };
  const areaPolygonForList = useMemo(() => {
    return keepOutAreasPolygon.map((area) => {
      const xs = area.coords.map(([x, _]) => x);
      const ys = area.coords.map(([_, y]) => y);
      const left = Math.min(...xs);
      const right = Math.max(...xs);
      const top = Math.min(...ys);
      const bottom = Math.max(...ys);
      const halfHeight = (bottom - top) / 2;
      const halfWidth = (right - left) / 2;

      return { id: area.keepout_area_id, x: left + halfWidth, y: top + halfHeight };
    });
  }, [keepOutAreasPolygon]);
  const zoomToAreaPolygon = (id: number) => {
    zoomToElement(`custom-area-link-${id}`);
  };

  const jukiTooltipStyle = (id): "left-aligned" | "right-aligned" => {
    const transformWrapper = transformRef.current?.state;
    const displayAreaRect = mapRef.current?.getBoundingClientRect();
    const juki = document.querySelector(`#machine-${id}`) as HTMLDivElement;
    const mapCenter = mapRef.current?.getBoundingClientRect?.().width / 2;

    if (transformWrapper && displayAreaRect && juki) {
      if (juki.getBoundingClientRect()?.left + MACHINE_ICON_SIZE / 2 >= mapCenter) {
        return "left-aligned";
      } else {
        return "right-aligned";
      }
    }

    return "left-aligned";
  };

  const markTooltipStyle = (id): "left-aligned" | "right-aligned" => {
    const transformWrapper = transformRef.current?.state;
    const displayAreaRect = mapRef.current?.getBoundingClientRect();
    const mark = document.querySelector(`#mark-${id}`) as HTMLDivElement;
    const mapCenter = mapRef.current?.getBoundingClientRect?.().width / 2;

    if (transformWrapper && displayAreaRect && mark) {
      if (mark.getBoundingClientRect()?.left + MACHINE_ICON_SIZE / 2 >= mapCenter) {
        return "left-aligned";
      } else {
        return "right-aligned";
      }
    }

    return "left-aligned";
  };

  const areaTooltipStyle = (id): "left-aligned" | "right-aligned" => {
    const transformWrapper = transformRef.current?.state;
    const displayAreaRect = mapRef.current?.getBoundingClientRect();
    const area = document.querySelector(`#area-${id}`) as HTMLDivElement;
    const mapCenter = mapRef.current?.getBoundingClientRect?.().width / 2;

    if (transformWrapper && displayAreaRect && area) {
      const areaCenter = area.getBoundingClientRect()?.left + selectedArea.w / 2;
      if (areaCenter >= mapCenter) {
        return "left-aligned";
      } else {
        return "right-aligned";
      }
    }

    return "left-aligned";
  };

  const customAreaTooltipStyle = (): "left-aligned" | "right-aligned" => {
    const imageRect = imageRef.current?.getBoundingClientRect();
    const mapCenter = mapRef.current?.getBoundingClientRect?.().width / 2;
    const customAreaCenterX = imageRect?.x + selectedAreaPolygon.centerX() / scale;

    return mapCenter < customAreaCenterX ? "left-aligned" : "right-aligned";
  };

  const arrowTooltipStyle = (): "left-aligned" | "right-aligned" => {
    const imageRect = imageRef.current?.getBoundingClientRect();
    const mapCenter = mapRef.current?.getBoundingClientRect?.().width / 2;
    const arrowCenterX = imageRect?.x + selectedArrow.centerX() / scale;

    return mapCenter < arrowCenterX ? "left-aligned" : "right-aligned";
  };

  const textTooltipStyle = (): "left-aligned" | "right-aligned" => {
    const imageRect = imageRef.current?.getBoundingClientRect();
    const mapCenter = mapRef.current?.getBoundingClientRect?.().width / 2;
    const textCenterX = imageRect?.x + selectedText.centerX() / scale;

    return mapCenter < textCenterX ? "left-aligned" : "right-aligned";
  };

  const setEditingDetailIdWithUpdate = useCallback((id: number | null, e?: React.MouseEvent) => {
    setSelectedPlotPlanDetailId(id);
    if (id === null) {
      setIsTooltipOpen(false);
    } else {
      setIsTooltipOpen(true);
      if (e && mapRef.current) {
        const mapCenter = mapRef.current.getBoundingClientRect().width / 2;
        const clickX = e.clientX - mapRef.current.getBoundingClientRect().left;
        setTooltipLeftOrRight(clickX >= mapCenter ? "left-aligned" : "right-aligned");
        e.stopPropagation();
      }
    }
  }, []);

  const [machineSubMenuRef, setMachineSubMenuRef] = useState(null);
  const [markSubMenuRef, setMarkSubMenuRef] = useState(null);
  const [keepOutAreaSubMenuRef, setKeepOutAreaSubMenuRef] = useState(null);
  const customAreaSubmenuDummyRef = useRef<HTMLDivElement>();

  // machine・area・mark・矢印・テキスト・多角形立入禁止エリアの選択状態をまとめて解除する
  const clearAllSelections = useCallback(() => {
    setSelectedMachine(null);
    machineMoveableRef.current = null;
    setMachineSubMenuRef(null);

    setSelectedArea(null);
    setWithMachine(null);
    areaMoveableRef.current = null;
    setKeepOutAreaSubMenuRef(null);

    setSelectedMark(null);
    markMoveableRef.current = null;
    setMarkSubMenuRef(null);

    // 自由描画オブジェクト（矢印・テキスト・多角形立入禁止エリア）の選択解除
    selectedArrow.end();
    selectedText.end();
    selectedTextRef.current = null;
    textMoveableRef.current = null;
    selectedAreaPolygon.end();

    setIsTooltipOpen(false);
  }, [selectedArrow, selectedText, selectedAreaPolygon]);

  const updateMachineStatus = (selectedMachine: Machine | null, changedUseEndHour?: string) => {
    if (!selectedMachine) {
      return;
    }
    changeMachineUseEndHourModal.submit(selectedMachine, changedUseEndHour, {
      onSuccess: (response) => {
        const status =
          selectedMachine.status === MachineStatus.STAND_BY ? MachineStatus.RUNNING : MachineStatus.STAND_BY;
        const update_date = response.timestamp.update_date;
        setSelectedMachine({
          ...selectedMachine,
          status,
          use_end_hour: changedUseEndHour ?? selectedMachine.use_end_hour,
          timestamp: {
            ...selectedMachine.timestamp,
            update_date,
          },
        });
        machineMoveableRef.current = null;
        if (status === MachineStatus.RUNNING) {
          dispatch(actions.plotmap.fetchMachines(Number(plotPlanId), layoutDate));
        }
      },
    });
  };

  const isDisabledMachineStatusButton = (useHour: string) => {
    // 未来の重機は稼動中にできない
    return moment().isBefore(formatUseHour(useHour));
  };

  const shouldChangeMachineUseEndHour = (useHour: string) => {
    // ToがNOW以前の場合、ステータスを稼動中に変更するのと同時に終了時間を変更する必要がある
    return moment().isSameOrAfter(formatUseHour(useHour));
  };

  // MiniMapの操作
  const [draggingReviewMap, setDraggingReviewMap] = useState(false);

  const getRelativeClickPosition = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const clickX = e.pageX;
    const clickY = e.pageY;

    const clientRect = e.currentTarget.getBoundingClientRect();
    const positionX = clientRect.left + window.pageXOffset;
    const positionY = clientRect.top + window.pageYOffset;

    return { x: clickX - positionX, y: clickY - positionY };
  };

  const updateReviewMap = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const position = getRelativeClickPosition(e);
    const { offsetWidth, offsetHeight } = e.currentTarget;
    let x = position.x - reviewMap.width / 2;
    let y = position.y - reviewMap.height / 2;

    if (x < 0) {
      x = 0;
    } else if (position.x + reviewMap.width / 2 > offsetWidth) {
      x = offsetWidth - reviewMap.width;
    }

    if (y < 0) {
      y = 0;
    } else if (position.y + reviewMap.height / 2 > offsetHeight) {
      y = offsetHeight - reviewMap.height;
    }

    const imageRect = imageRef.current.getBoundingClientRect();
    const widthRatio = imageRect.width / offsetWidth;
    const heightRatio = imageRect.height / offsetHeight;

    const { setTransform, state } = transformRef.current;
    setTransform(x * -widthRatio, y * -heightRatio, state.scale, 0);
    setReviewMap({ ...reviewMap, top: y - 2, left: x - 2 }); // -2はborderの幅分
  };

  const handleDragStart = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setDraggingReviewMap(true);
    updateReviewMap(e);
  };

  const handleDragging = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (draggingReviewMap) {
      updateReviewMap(e);
    }
  };

  const handleDragEnd = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setDraggingReviewMap(false);
  };

  const handleCreateSubMachine = (machine: Machine) => {
    const { x: copyX, y: copyY } = getCopyMachineCoords(
      {
        x: machine.x ?? 0,
        y: machine.y ?? 0,
      },
      { width: imageRef.current?.offsetWidth ?? 0, height: imageRef.current?.offsetHeight ?? 0, ratio: mapRatio }
    );
    dispatch(
      actions.machineEdit.createSubMachine({
        input: { machine_id: machine.machine_id, x: copyX, y: copyY },
        onSuccess: () => {
          setSelectedMachine(null);
          selectedMachineRef.current = null;
          // 重機情報を再取得する
          dispatch(actions.plotmap.fetchMachines(Number(plotPlanId), layoutDate));
        },
      })
    );
  };

  return (
    <div className="bg-[#f3f2ef] min-h-screen">
      <Header />
      {isTooltipOpen && !!selectedArea && (
        <TooltipKeepOutArea
          data={selectedArea}
          leftOrRight={areaTooltipStyle(selectedArea.keepout_area_id)}
          machine={machines.find((m) => m.machine_id === selectedArea.machine_id)}
        />
      )}
      {isTooltipOpen && selectedAreaPolygon.freeDrawArea && (
        <TooltipKeepOutArea data={selectedAreaPolygon.freeDrawArea} leftOrRight={customAreaTooltipStyle()} />
      )}
      {isTooltipOpen && !!selectedMachine && (
        <TooltipMachine data={selectedMachine} leftOrRight={jukiTooltipStyle(selectedMachine.machine_id)} />
      )}
      {isTooltipOpen && !!selectedMark && (
        <TooltipMark data={selectedMark} leftOrRight={markTooltipStyle(selectedMark.mark_id)} />
      )}
      {isTooltipOpen && !!selectedArrow.freeDrawArrow && (
        <TooltipArrow data={selectedArrow.freeDrawArrow} leftOrRight={arrowTooltipStyle()} />
      )}
      {isTooltipOpen && !!selectedText.freeDrawText && (
        <TooltipText data={selectedText.freeDrawText} leftOrRight={textTooltipStyle()} />
      )}
      {isTooltipOpen && !!selectedPlotPlanDetailId && (
        <TooltipPlotPlanDetail plotPlanName={selectedPlotPlanDetailName} leftOrRight={tooltipLeftOrRight} />
      )}
      <div id="contents" ref={contentRef} className="flex flex-col home px-8 h-[calc(100vh_-_65px)] overflow-hidden">
        <Toolbar
          ref={menuRef}
          canEditMap={navigation.editable}
          disabled={isPast || isSpectator || !navigation.editable}
          plotPlan={plotPlan}
          overviewPlotPlanId={plotPlan?.plot_plan_type === PlotPlanType.DETAIL ? overviewPlotPlanId : undefined}
          clipAreas={clipAreas}
          loggedInUserCompanyId={loginUserCompanyId}
          onChangePlotPlan={() => plotPlanSelector.open()}
          onCreateMachine={() => {
            clearAllSelections();
            machineEditorModal.open();
          }}
          onCreateArea={() => {
            clearAllSelections();
            keepOutAreaEditorModal.open();
          }}
          onCreateCustomArea={() => {
            clearAllSelections();
            drawingArea.init();
          }}
          onCreateMark={() => {
            clearAllSelections();
            markEditorModal.open();
          }}
          onChangeEditable={(e) => navigation.setEditable(e.target.checked)}
          onAddText={() => {
            clearAllSelections();
            editTextModal.open();
          }}
          onStartDrawingArrow={() => {
            clearAllSelections();
            drawingArrow.init();
          }}
          onReload={() => {
            dispatch(actions.plotmap.fetchMachines(Number(plotPlanId), layoutDate));
            dispatch(actions.plotmap.fetchMarks(Number(plotPlanId), layoutDate));
            dispatch(actions.plotmap.fetchKeepOutAreas(Number(plotPlanId), layoutDate));
            dispatch(actions.plotmap.fetchArrows(Number(plotPlanId), layoutDate));
            dispatch(actions.plotmap.fetchTexts(Number(plotPlanId), layoutDate));
            refreshPlotPlanDetails();
          }}
          scale={scale}
          mapRatio={mapRatio}
        />
        <TransformWrapper
          // NOTE: TransformWrapper を初期化するためにプロットプラン切り替え時に再レンダリングする
          key={`transform-wrapper-${plotPlanId}`}
          ref={transformRef}
          centerZoomedOut
          initialScale={1}
          minScale={minScale}
          maxScale={maxScale}
          pinch={{ disabled: true }}
          panning={{
            disabled:
              selectedMachine != null ||
              selectedArea != null ||
              selectedMark != null ||
              !!selectedArrow.freeDrawArrow ||
              !!selectedText.freeDrawText ||
              !!selectedAreaPolygon.freeDrawArea,
            velocityDisabled: true,
          }}
          doubleClick={{ disabled: true }}
          velocityAnimation={{ disabled: true }}
          alignmentAnimation={{ disabled: true, sizeX: 0, sizeY: 0 }}
          onPanning={updateNavigationArea}
          onWheel={updateNavigationArea}
        >
          <div ref={operationRef}>
            <NavigationToolbar
              displayAreaDetail={navigation.displayAreaDetail}
              opacity={navigation.opacity}
              displayNavi={navigation.displayNavi}
              onChangeDisplayAreaDetail={navigation.setDisplayAreaDetail}
              onChangeOpacity={(opacity) => navigation.setOpacity(opacity)}
              onChangeMapDisplayNavi={(displayNavi) => navigation.setDisplayNavi(displayNavi)}
              onZoomOut={handleZoomOut}
              onZoomIn={handleZoomIn}
              magnificationRatio={magnificationRatio}
              onDisplayHelpModal={() => helpModal.open()}
              disableFreeDraw={!navigation.editable}
              plotPlan={plotPlan}
              // Custom area
              isCustomAreaDrawing={drawingArea.isDrawing}
              onCancelDrawingArea={() => drawingArea.clear()}
              // Arrow
              isArrowDrawing={drawingArrow.isDrawing}
              selectedArrow={selectedArrow.freeDrawArrow}
              arrowColor={drawingArrow.isDrawing ? drawingArrow.color : selectedArrow.freeDrawArrow?.color}
              arrowWidth={drawingArrow.isDrawing ? drawingArrow.thickness : selectedArrow.freeDrawArrow?.thickness}
              onClickEditArrow={() => {
                editArrowModal.open();
                setIsTooltipOpen(false);
              }}
              onClickCancelArrow={() => {
                drawingArrow.end();
                selectedArrow.end();
              }}
              onClickCopyArrow={() => {
                copyArrowModal.open();
                setIsTooltipOpen(false);
              }}
              onClickDeleteArrow={() => {
                deleteArrowModal.open();
                setIsTooltipOpen(false);
              }}
              // Text
              selectedText={selectedText.freeDrawText}
              textColor={selectedText.freeDrawText ? selectedText.freeDrawText.color : loginUserCompanyIconColor}
              textSize={selectedText.freeDrawText ? selectedText.freeDrawText.font_size : 12}
              onClickEditText={() => {
                editTextModal.open();
                setIsTooltipOpen(false);
              }}
              onClickCopyText={() => {
                copyTextModal.open();
                setIsTooltipOpen(false);
              }}
              onClickDeleteText={() => {
                deleteTextModal.open();
                setIsTooltipOpen(false);
              }}
            />
          </div>
          <div
            ref={mapRef}
            className="flex items-center justify-center border border-solid grow bg-[#ccc] relative overflow-hidden mb-[10px]"
          >
            <TransformComponent
              contentStyle={{ width: "100%" }}
              wrapperStyle={{
                width: "100%",
                height: "100%",
                position: "relative",
              }}
            >
              <div className={`${!mapImage ? "hidden" : ""} relative w-full`}>
                <img
                  className="w-full"
                  ref={imageRef}
                  src={mapImage}
                  onLoad={handleImageLoaded}
                  onClick={() => setEditingDetailIdWithUpdate(null)}
                />
                <div
                  className={`${
                    navigation.opacity ? "" : "hidden"
                  } pointer-events-none absolute top-0 left-0 w-full h-full bg-[rgba(255,255,255,0.5)]`}
                />
                {/* クリップ領域 */}
                {clipAreas.map((clip) => (
                  <div
                    key={clip.plot_plan_mapping_id}
                    className="absolute border-2 border-dashed border-[#cc0000] box-border pointer-events-none"
                    style={{
                      top: clip.clip_y * mapRatio,
                      left: clip.clip_x * mapRatio,
                      width: clip.clip_w * mapRatio,
                      height: clip.clip_h * mapRatio,
                    }}
                  />
                ))}
                {/* 矢印・テキスト・多角形立入禁止エリア (Konvaで描画) */}
                <div ref={drawScreenElem} className="absolute top-0 left-0 w-full h-full">
                  <FreeDrawMain
                    stageSize={stageSize}
                    mapRatio={mapRatio}
                    isModalOpen={
                      editArrowModal.isOpen ||
                      copyArrowModal.isOpen ||
                      deleteArrowModal.isOpen ||
                      editTextModal.isOpen ||
                      copyTextModal.isOpen ||
                      deleteTextModal.isOpen ||
                      keepOutAreaEditorModal.isOpen ||
                      copyCustomAreaModal.isOpen ||
                      deleteCustomAreaModal.isOpen
                    }
                    // 矢印
                    arrows={arrows}
                    selectedArrow={selectedArrow.freeDrawArrow}
                    isArrowEditable={selectedArrow.isEditable}
                    onClickArrow={(arrow, rect) => {
                      selectedText.end();
                      selectedTextRef.current = null;
                      textMoveableRef.current = null;
                      selectedAreaPolygon.end();
                      selectedArrow.start(arrow, rect);
                      setSelectedPlotPlanDetailId(null);
                      setIsTooltipOpen(true);
                    }}
                    onMoveArrow={(arrow: FreeDrawArrow) => selectedArrow.changeCoords(arrow)}
                    onDragArrowEnd={(rect) => selectedArrow.endDrag(rect)}
                    onSelectArrowEnd={selectedArrow.end}
                    // テキスト（詳細プロットプランのときのみ）
                    {...(plotPlan?.plot_plan_type === PlotPlanType.DETAIL && {
                      texts,
                      selectedText: selectedText.freeDrawText,
                      isTextEditable: selectedText.isEditable,
                      onClickText: (text, rect) => {
                        selectedArrow.end();
                        selectedAreaPolygon.end();
                        selectedText.start(text, rect);
                        setSelectedPlotPlanDetailId(null);
                        setIsTooltipOpen(true);
                      },
                      onMoveText: selectedText.changeCoord,
                      onDragTextEnd: (rect) => selectedText.endDrag(rect),
                      onSelectTextEnd: selectedText.end,
                    })}
                    // 立入禁止エリア
                    keepOutAreaPolygon={
                      wipKeepOutAreaPolygon.value
                        ? [...keepOutAreasPolygon, wipKeepOutAreaPolygon.value]
                        : keepOutAreasPolygon
                    }
                    selectedArea={selectedAreaPolygon.freeDrawArea}
                    isAreaEditable={selectedAreaPolygon.isEditable}
                    onClickArea={(area, rect) => {
                      selectedArrow.end();
                      selectedText.end();
                      selectedTextRef.current = null;
                      textMoveableRef.current = null;
                      selectedAreaPolygon.start(area, rect);
                      setSelectedPlotPlanDetailId(null);
                      setIsTooltipOpen(true);
                    }}
                    onMoveArea={(area: KeepOutAreaPolygon) => {
                      selectedAreaPolygon.changeCoords(area);
                    }}
                    onDragAreaStart={selectedAreaPolygon.startDrag}
                    onDragAreaEnd={(rect) => selectedAreaPolygon.endDrag(rect)}
                    onSelectAreaEnd={selectedAreaPolygon.end}
                    disabled={!navigation.editable}
                  />
                </div>
                <div ref={customAreaSubmenuDummyRef} />
                {navigation.editable &&
                  selectedAreaPolygon.freeDrawArea &&
                  selectedAreaPolygon.rect &&
                  !selectedAreaPolygon.dragging &&
                  selectedAreaPolygon.hasRole && (
                    <CustomKeepOutAreaSubMenu
                      area={selectedAreaPolygon.freeDrawArea}
                      mapRef={mapRef.current}
                      referenceElement={customAreaSubmenuDummyRef.current}
                      rect={selectedAreaPolygon.rect}
                      scale={scale}
                      control={{
                        isPast,
                        isAdmin,
                        isUser,
                        isAreaFromOtherCompany,
                      }}
                      onEdit={(e) => {
                        e.stopPropagation();
                        keepOutAreaEditorModal.open();
                        setIsTooltipOpen(false);
                      }}
                      onDuplicate={(e) => {
                        e.stopPropagation();
                        copyCustomAreaModal.open();
                        setIsTooltipOpen(false);
                      }}
                      onDelete={(e) => {
                        e.stopPropagation();
                        deleteCustomAreaModal.open();
                        setIsTooltipOpen(false);
                      }}
                    />
                  )}
              </div>
              {/* 詳細プロットプラン（色付き矩形 + 重機・立入禁止エリア） */}
              {mapImage &&
                [...plotPlanDetails]
                  .sort((a, b) => {
                    const aOrderIndex = plotPlanDetailOrder.indexOf(a.detail_plot_plan_id);
                    const bOrderIndex = plotPlanDetailOrder.indexOf(b.detail_plot_plan_id);
                    const aOriginalIndex = plotPlanDetails.indexOf(a);
                    const bOriginalIndex = plotPlanDetails.indexOf(b);
                    // クリック済みのものはorderの順（末尾ほど前面）、未クリックは元の順序で先頭に
                    const aEffective = aOrderIndex === -1 ? aOriginalIndex - plotPlanDetails.length : aOrderIndex;
                    const bEffective = bOrderIndex === -1 ? bOriginalIndex - plotPlanDetails.length : bOrderIndex;
                    return aEffective - bEffective;
                  })
                  .map((detail) => {
                    return (
                      <PlotPlanDetailObject
                        key={detail.detail_plot_plan_id}
                        plotPlanMapping={detail}
                        mapRatio={mapRatio}
                        displayAreaDetail={navigation.displayAreaDetail}
                        onClickPlotPlanDetail={(id, e) => {
                          selectedArrow.end();
                          selectedText.end();
                          selectedTextRef.current = null;
                          textMoveableRef.current = null;
                          selectedAreaPolygon.end();
                          setSelectedPlotPlanDetailId(id);
                          setPlotPlanDetailOrder((prev) => {
                            const without = prev.filter((v) => v !== id);
                            return [...without, id];
                          });
                          setIsTooltipOpen(true);
                          if (mapRef.current) {
                            const mapCenter = mapRef.current.getBoundingClientRect().width / 2;
                            const clickX = e.clientX - mapRef.current.getBoundingClientRect().left;
                            setTooltipLeftOrRight(clickX >= mapCenter ? "left-aligned" : "right-aligned");
                          }
                        }}
                        onDoubleClickPlotPlanDetail={(id) => {
                          setSelectedPlotPlanDetailId(null);
                          setOverviewPlotPlanId(Number(plotPlanId));
                          dispatch(actions.plotmap.selectPlotPlan(String(id)));
                          storageManager.setPlotPlanId(id);
                        }}
                      />
                    );
                  })}
              {/* テキスト（全体プロットプランのとき） */}
              {mapImage && plotPlan?.plot_plan_type === PlotPlanType.OVERVIEW && (
                <div className="text-wrapper">
                  {texts.map((text) => (
                    <TextObject
                      id={`text-${text.freetext_id}`}
                      key={`text-${text.freetext_id}`}
                      text={text}
                      mapRatio={mapRatio}
                      selected={selectedText.freeDrawText?.freetext_id === text.freetext_id}
                      onClick={() => {
                        selectedArrow.end();
                        selectedAreaPolygon.end();
                        const textElem = document.querySelector<HTMLDivElement>(`#text-${text.freetext_id}`);
                        const rect = textElem
                          ? {
                              x: Math.round(text.x * mapRatio),
                              y: Math.round(text.y * mapRatio),
                              width: textElem.offsetWidth,
                              height: textElem.offsetHeight,
                            }
                          : null;
                        selectedText.start(text, rect);
                        setSelectedPlotPlanDetailId(null);
                        setIsTooltipOpen(true);
                        if (navigation.editable) {
                          selectedTextRef.current = textElem;
                          textMoveableRef.current = document.querySelector(`#text-moveable-${text.freetext_id}`);
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: Math.round(text.y * mapRatio),
                        left: Math.round(text.x * mapRatio),
                      }}
                    />
                  ))}
                  {/* テキスト移動（OVERVIEWのときのみ） */}
                  <Moveable
                    target={plotPlan?.plot_plan_type === PlotPlanType.OVERVIEW ? textMoveableRef.current : null}
                    origin={false}
                    draggable={plotPlan?.plot_plan_type === PlotPlanType.OVERVIEW && !isPast && selectedText.isEditable}
                    throttleDrag={0}
                    onDrag={moveableText.onDrag}
                    onDragEnd={moveableText.onDragEnd}
                  />
                </div>
              )}
              {/* 立入禁止エリア */}
              <div className="area-wrapper [&_.moveable-control-box]:z-0">
                {/* 非表示項目：カスタム立入禁止エリア(konvaで描画)の一覧から選択して移動するためのエレメント */}
                {areaPolygonForList.map((area) => (
                  <div
                    id={`custom-area-link-${area.id}`}
                    key={`custom-area-link-${area.id}`}
                    style={{
                      position: "absolute",
                      top: Math.round(area.y * mapRatio),
                      left: Math.round(area.x * mapRatio),
                      width: 0,
                      height: 0,
                    }}
                  />
                ))}
                {mapImage &&
                  keepOutAreas.map((area) => (
                    <KeepOutAreaObject
                      id={`area-${area.keepout_area_id}`}
                      key={`area-${area.keepout_area_id}`}
                      area={area}
                      machine={machines.find((m) => m.machine_id === area.machine_id)}
                      onClick={() => {
                        setSelectedArea(area);
                        setIsTooltipOpen(true);
                        areaMoveableRef.current = document.querySelector(`#area-moveable-${area.keepout_area_id}`);
                        if (navigation.editable) {
                          setWithMachine(machines.find((v) => area.machine_id === v.machine_id));
                          selectedAreaRef.current = document.querySelector(`#area-${area.keepout_area_id}`);
                          setKeepOutAreaSubMenuRef(document.querySelector(`#area-${area.keepout_area_id}`));
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: Math.round(area.y * mapRatio),
                        left: Math.round(area.x * mapRatio),
                        width: Math.round(area.w * mapRatio),
                        height: Math.round(area.h * mapRatio),
                        background: `#${area.icon_color}`,
                      }}
                    />
                  ))}

                {!isDragging && !isAnyModalOpen && selectedArea && (
                  <KeepOutAreaSubMenu
                    area={selectedArea}
                    referenceElement={keepOutAreaSubMenuRef}
                    scale={scale}
                    control={{
                      isPast,
                      isAdmin,
                      isUser,
                      isAreaFromOtherCompany,
                    }}
                    onEdit={(e) => {
                      e.stopPropagation();
                      areaMoveableRef.current = null;
                      setWithMachine(machines.find((v) => selectedArea.machine_id === v.machine_id));
                      keepOutAreaEditorModal.open();
                      setIsTooltipOpen(false);
                    }}
                    onDuplicate={(e) => {
                      e.stopPropagation();
                      areaMoveableRef.current = null;
                      copyModal.openKeepOutArea(selectedArea);
                      setIsTooltipOpen(false);
                    }}
                    onDelete={(e) => {
                      e.stopPropagation();
                      areaMoveableRef.current = null;
                      deleteAreaModal.open();
                      setIsTooltipOpen(false);
                    }}
                  />
                )}
                {/* 立入禁止エリア移動 */}
                <Moveable
                  target={areaMoveableRef.current}
                  draggable={navigation.editable && !isPast && (isAdmin || (isUser && !isAreaFromOtherCompany))}
                  resizable={navigation.editable && !isPast && (isAdmin || (isUser && !isAreaFromOtherCompany))}
                  keepRatio={false}
                  throttleResize={1}
                  edge={false}
                  zoom={scale}
                  origin={false}
                  padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
                  throttleDrag={0}
                  onDrag={moveableArea.onDrag}
                  onDragEnd={moveableArea.onDragEnd}
                  onResizeStart={moveableArea.onResizeStart}
                  onResize={moveableArea.onResize}
                  onResizeEnd={moveableArea.onResizeEnd}
                />
              </div>
              {/* マーク */}
              <div className="mark-wrapper">
                {/* マークオブジェクト */}
                {mapImage &&
                  marks.map((mark) => (
                    <MarkObject
                      key={`mark-${mark.mark_id}`}
                      id={`mark-${mark.mark_id}`}
                      mark={mark}
                      selected={selectedMark && mark.mark_id === selectedMark.mark_id}
                      onClick={() => {
                        setSelectedMark(mark);
                        setIsTooltipOpen(true);
                        if (navigation.editable) {
                          selectedMarkRef.current = document.querySelector(`#mark-${mark.mark_id}`);
                          markMoveableRef.current = document.querySelector(`#mark-moveable-${mark.mark_id}`);
                          setMarkSubMenuRef(document.querySelector(`#mark-${mark.mark_id}`));
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: Math.round(mark.y * mapRatio) - (MACHINE_ICON_SIZE * (mark.size ?? 1)) / 2,
                        left: Math.round(mark.x * mapRatio) - (MACHINE_ICON_SIZE * (mark.size ?? 1)) / 2,
                        width: MACHINE_ICON_SIZE * (mark.size ?? 1),
                        height: MACHINE_ICON_SIZE * (mark.size ?? 1),
                      }}
                    />
                  ))}
                {/* マークのサブメニュー */}
                {!isDragging && !isAnyModalOpen && selectedMark && (
                  <MarkSubMenu
                    referenceElement={markSubMenuRef}
                    mark={selectedMark}
                    scale={scale}
                    control={{
                      isPast,
                      isToday,
                      isAdmin,
                      isUser,
                      isMarkFromOtherCompany,
                    }}
                    onEdit={(e) => {
                      e.stopPropagation();
                      markMoveableRef.current = null;
                      markEditorModal.open();
                      setIsTooltipOpen(false);
                    }}
                    onDuplicate={(e) => {
                      e.stopPropagation();
                      markMoveableRef.current = null;
                      copyMarkModal.open(selectedMark);
                      setIsTooltipOpen(false);
                    }}
                    onDelete={(e) => {
                      e.stopPropagation();
                      markMoveableRef.current = null;
                      deleteMarkModal.open();
                      setIsTooltipOpen(false);
                    }}
                    onChangeSize={(m: Mark) => {
                      setSelectedMark(m);
                    }}
                  />
                )}
                {/* マーク移動 */}
                <Moveable
                  target={markMoveableRef.current}
                  origin={false}
                  draggable={true}
                  throttleDrag={0}
                  onDrag={moveableMark.onDrag}
                  onDragEnd={moveableMark.onDragEnd}
                />
              </div>
              {/* 重機 */}
              <div className="machine-wrapper">
                {mapImage &&
                  machines.map((data) => (
                    <MachineObject
                      id={`machine-${data.machine_id}`}
                      key={`machine-${data.machine_id}`}
                      machine={data}
                      selected={selectedMachine && data.machine_id === selectedMachine.machine_id}
                      onClick={() => {
                        setSelectedMachine(data);
                        setIsTooltipOpen(true);
                        if (navigation.editable) {
                          selectedMachineRef.current = document.querySelector(`#machine-${data.machine_id}`);
                          machineMoveableRef.current = document.querySelector(`#machine-moveable-${data.machine_id}`);
                          setMachineSubMenuRef(document.querySelector(`#machine-${data.machine_id}`));
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: Math.round(data.y * mapRatio) - (MACHINE_ICON_SIZE * (data.size ?? 1)) / 2,
                        left: Math.round(data.x * mapRatio) - (MACHINE_ICON_SIZE * (data.size ?? 1)) / 2,
                        width: MACHINE_ICON_SIZE * (data.size ?? 1),
                        height: MACHINE_ICON_SIZE * (data.size ?? 1),
                      }}
                    />
                  ))}

                {!isDragging && !isAnyModalOpen && selectedMachine && (
                  <MachineSubMenu
                    referenceElement={machineSubMenuRef}
                    machine={selectedMachine}
                    scale={scale}
                    control={{
                      isPast,
                      isToday,
                      isAdmin,
                      isUser,
                      isMachineFromOtherCompany,
                      isDisableMachineStatusButton: isDisabledMachineStatusButton(selectedMachine?.use_start_hour),
                    }}
                    onEdit={(e) => {
                      e.stopPropagation();
                      machineMoveableRef.current = null;
                      machineEditorModal.open();
                      setIsTooltipOpen(false);
                    }}
                    onDuplicateLater={(e) => {
                      e.stopPropagation();
                      machineMoveableRef.current = null;
                      copyModal.openMachine(selectedMachine);
                      setIsTooltipOpen(false);
                    }}
                    onDuplicateToday={(e) => {
                      e.stopPropagation();
                      machineMoveableRef.current = null;
                      machineEditorModal.open({ duplicate: true, x: selectedMachine.x, y: selectedMachine.y });
                      setIsTooltipOpen(false);
                    }}
                    onCreateSubMachine={(e) => {
                      e.stopPropagation();
                      machineMoveableRef.current = null;
                      handleCreateSubMachine(selectedMachine);
                      setIsTooltipOpen(false);
                    }}
                    onDelete={(e) => {
                      e.stopPropagation();
                      machineMoveableRef.current = null;
                      deleteMachineModal.open();
                      setIsTooltipOpen(false);
                    }}
                    onCreateKeepOutArea={(e) => {
                      e.stopPropagation();
                      machineMoveableRef.current = null;
                      setWithMachine(selectedMachine);
                      keepOutAreaEditorModal.open();
                      setIsTooltipOpen(false);
                    }}
                    onChangeSize={(m: Machine) => {
                      setSelectedMachine(m);
                    }}
                    onChangeStatus={(e) => {
                      e.stopPropagation();

                      if (
                        // 使用時間に関わらず作業完了をクリックした際はステータス変更を行う
                        selectedMachine.status !== MachineStatus.RUNNING &&
                        shouldChangeMachineUseEndHour(selectedMachine?.use_end_hour)
                      ) {
                        changeMachineUseEndHourModal.open();

                        return;
                      }

                      updateMachineStatus(selectedMachine);
                    }}
                  />
                )}
                {/* 重機移動 */}
                <Moveable
                  target={machineMoveableRef.current}
                  origin={false}
                  draggable={true}
                  throttleDrag={0}
                  onDrag={moveableMachine.onDrag}
                  onDragEnd={moveableMachine.onDragEnd}
                />
              </div>
              <div className={`${drawingArrow.isDrawing ? "" : "hidden"} absolute top-0 left-0 w-full h-full`}>
                <ArrowDrawStage
                  isDrawing={drawingArrow.isDrawing}
                  mainScreenElem={drawScreenElem.current}
                  mapRatio={mapRatio}
                  scale={scale}
                  plotPlanId={Number(plotPlanId)}
                  drawingCompanyId={drawingArrow.companyId}
                  drawingColor={drawingArrow.color}
                  drawingThickness={drawingArrow.thickness}
                  drawingUseStartHour={drawingArrow.useStartHour}
                  drawingUseEndHour={drawingArrow.useEndHour}
                  drawingCoords={drawingArrow.coords}
                  editing={editArrowModal.isOpen}
                  onAddFreeDrawingCoord={(coord: [number, number]) => drawingArrow.addCoord(coord)}
                  onFreeDrawEnd={drawingArrow.end}
                />
              </div>
              <div className={`${drawingArea.isDrawing ? "" : "hidden"} absolute top-0 left-0 w-full h-full`}>
                <KeepOutAreaPolygonDrawStage
                  isDrawing={drawingArea.isDrawing}
                  mainScreenElem={drawScreenElem.current}
                  mapRatio={mapRatio}
                  scale={scale}
                  drawingCoords={drawingArea.coords}
                  onAddFreeDrawingCoord={(coord: [number, number]) => drawingArea.addCoord(coord)}
                  onAddKeepOutArea={() => {
                    keepOutAreaEditorModal.open();
                    wipKeepOutAreaPolygon.add({
                      icon_color: loginUserCompanyIconColor?.replace("#", ""),
                      coords: drawingArea.coords,
                    });
                    drawingArea.end();
                  }}
                  onFreeDrawClear={drawingArea.clear}
                />
              </div>
            </TransformComponent>
            {/* ナビ */}
            <div
              onMouseDown={handleDragStart}
              onMouseMove={handleDragging}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
              className={
                !!navigation.displayNavi && !!mapImageBlob
                  ? "absolute top-[10px] right-[10px] shadow-lg border-2 border-[#888] border-solid z-auto"
                  : "hidden"
              }
              style={{
                width: REVIEW_MAP_WIDTH,
                height: reviewMap.height ? "auto" : REVIEW_MAP_HEIGHT,
              }}
            >
              <img className="pointer-events-none" src={mapImage} />
              <div
                className="absolute border-2 border-[#c00] border-solid pointer-events-none"
                style={{ ...reviewMap }}
              />
            </div>
          </div>
        </TransformWrapper>
        {/* オブジェクトの一覧 */}
        <ObjectList
          ref={tableRef}
          isOpen={navigation.showMachineList}
          onToggleList={navigation.toggleMachineList}
          onZoomMachine={zoomToMachine}
          onZoomAreaRectangle={zoomToAreaRectangle}
          onZoomAreaPolygon={zoomToAreaPolygon}
          onZoomMark={zoomToMark}
          disabled={plotPlan?.plot_plan_type === PlotPlanType.OVERVIEW}
        />
        {/* 立入禁止エリア編集モーダル */}
        {keepOutAreaEditorModal.isOpen && (
          <KeepOutAreaEdit
            keepOutAreaData={selectedArea || selectedAreaPolygon.freeDrawArea}
            plotPlanId={Number(plotPlanId)}
            onClose={() => {
              keepOutAreaEditorModal.close();
              setSelectedArea(null);
              setWithMachine(null);
              selectedAreaPolygon.end();
              wipKeepOutAreaPolygon.remove();
              drawingArea.clear();
              selectedAreaRef.current = null;
            }}
            initialX={initialPosition.keepOutArea.initialX}
            initialY={initialPosition.keepOutArea.initialY}
            withMachine={withMachine}
            customCoords={selectedAreaPolygon.freeDrawArea?.coords || drawingArea.coords}
            onChangeAreaCompany={(c) =>
              wipKeepOutAreaPolygon.changeCompany({ company_id: c.company_id, icon_color: c.icon_color })
            }
          />
        )}
        {plotPlanSelector.isOpen && (
          <PlotPlanSelector options={plotPlanSelectorOptions} onClose={() => plotPlanSelector.close()} />
        )}
        {/* 重機編集モーダル */}
        {machineEditorModal.isOpen && (
          <MachineEditor
            machineData={selectedMachine}
            duplicate={machineEditorModal.duplicate}
            plotPlanId={plotPlanId}
            onClose={() => {
              machineEditorModal.close();
              setSelectedMachine(null);
              selectedMachineRef.current = null;
            }}
            initialX={machineEditorModal.duplicate ? machineEditorModal.x : initialPosition.machine.initialX}
            initialY={machineEditorModal.duplicate ? machineEditorModal.y : initialPosition.machine.initialY}
          />
        )}

        {/* 重機複製モーダル */}
        {copyModal.target === "machine" && (
          <CopyMachineKeepOutAreaModal
            isCopyMachine={true}
            onClose={() => {
              copyModal.close();
              setSelectedMachine(null);
              selectedMachineRef.current = null;
            }}
            onSubmit={({ layoutDateFrom, layoutDateTo, copyOption }) => {
              if (copyOption === ASSOCIATED_COPY) {
                copyModal.copyMachineWithKeepOutArea(layoutDateFrom, layoutDateTo);
              } else {
                copyModal.copyMachine(layoutDateFrom, layoutDateTo);
              }
            }}
            machineId={copyModal.machineId.toString()}
            keepOutAreaId={copyModal.keepOutAreaId?.toString()}
          />
        )}

        {/* 重機削除モーダル */}
        {deleteMachineModal.isOpen && (
          <DeleteMachineModal
            machine={selectedMachine}
            onClose={() => {
              deleteMachineModal.close();
              setSelectedMachine(null);
              selectedMachineRef.current = null;
            }}
            onSuccess={() => {
              dispatch(actions.plotmap.fetchMachines(Number(plotPlanId), layoutDate));
            }}
          />
        )}

        {/* 終了時間調整モーダル */}
        {changeMachineUseEndHourModal.isOpen && (
          <ChangeMachineUseEndHourModal
            onClose={() => {
              changeMachineUseEndHourModal.close();
              setSelectedMachine(null);
              selectedMachineRef.current = null;
            }}
            onSubmit={({ useEndHour }) => {
              updateMachineStatus(selectedMachine, useEndHour);
            }}
            useStartHourProp={selectedMachine?.use_start_hour}
            useEndHourProp={selectedMachine?.use_end_hour}
          />
        )}

        {/* 立ち入り禁止エリア複製モーダル */}
        {copyModal.target === "area" && (
          <CopyMachineKeepOutAreaModal
            isCopyKeepOutArea={true}
            onClose={() => {
              copyModal.close();
              setSelectedArea(null);
              setWithMachine(null);
              selectedAreaRef.current = null;
            }}
            onSubmit={({ layoutDateFrom, layoutDateTo, copyOption }) => {
              if (copyOption === ASSOCIATED_COPY) {
                copyModal.copyMachineWithKeepOutArea(layoutDateFrom, layoutDateTo);
              } else {
                copyModal.copyKeepOutArea(layoutDateFrom, layoutDateTo);
              }
            }}
            machineId={copyModal.machineId?.toString()}
            keepOutAreaId={copyModal.keepOutAreaId.toString()}
          />
        )}

        {/* 立ち入り禁止エリア削除モーダル */}
        {deleteAreaModal.isOpen && (
          <DeleteKeepOutAreaModal
            area={selectedArea}
            onClose={() => {
              deleteAreaModal.close();
              setSelectedArea(null);
              setWithMachine(null);
              selectedAreaRef.current = null;
            }}
            onSuccess={() => {
              setSelectedMachine((prev) => ({
                ...prev,
                keepout_area_id: null,
              }));
            }}
          />
        )}

        {/* カスタム立入禁止エリア複製モーダル */}
        {copyCustomAreaModal.isOpen && (
          <CopyObjectModal
            onClose={() => {
              copyCustomAreaModal.close();
              selectedAreaPolygon.end();
            }}
            onSubmit={(dateRange: { layoutDateFrom: string; layoutDateTo: string }) => {
              dispatch(
                actions.keepOutAreas.copyKeepOutAreas({
                  input: {
                    keepout_area_id: selectedAreaPolygon.freeDrawArea.keepout_area_id.toString(),
                    layout_date_from: dateRange.layoutDateFrom,
                    layout_date_to: dateRange.layoutDateTo,
                  },
                  onSuccess: () => {
                    copyCustomAreaModal.close();
                    selectedAreaPolygon.end();
                  },
                })
              );
            }}
          />
        )}

        {/* カスタム立入禁止エリア削除モーダル */}
        {deleteCustomAreaModal.isOpen && (
          <DeleteConfirmModal
            id="custom-area-delete-modal"
            message={t("remove_keep_out_area_sure")}
            onClose={() => {
              deleteCustomAreaModal.close();
              selectedAreaPolygon.end();
            }}
            onSubmit={() => {
              dispatch(
                actions.keepOutAreas.deleteKeepOutAreas({
                  input: {
                    keepout_area_id: selectedAreaPolygon.freeDrawArea.keepout_area_id,
                    timestamp: selectedAreaPolygon.freeDrawArea.timestamp,
                  },
                  onSuccess: () => {
                    deleteCustomAreaModal.close();
                    selectedAreaPolygon.end();
                  },
                })
              );
            }}
          />
        )}

        {/* マーク編集モーダル */}
        {markEditorModal.isOpen && (
          <MarkEditor
            markData={selectedMark}
            duplicate={markEditorModal.duplicate}
            plotPlanId={plotPlanId}
            onClose={() => {
              markEditorModal.close();
              setSelectedMark(null);
              selectedMarkRef.current = null;
            }}
            initialX={markEditorModal.duplicate ? markEditorModal.x : initialPosition.machine.initialX}
            initialY={markEditorModal.duplicate ? markEditorModal.y : initialPosition.machine.initialY}
          />
        )}
        {/* マーク複製モーダル*/}
        {copyMarkModal.markId && (
          <CopyObjectModal
            onClose={copyMarkModal.close}
            onSubmit={(dateRange: { layoutDateFrom: string; layoutDateTo: string }) => {
              dispatch(
                actions.mark.copyMark(
                  {
                    mark_id: selectedMark.mark_id,
                    layout_date_from: dateRange.layoutDateFrom,
                    layout_date_to: dateRange.layoutDateTo,
                  },
                  () => {
                    setSelectedMark(null);
                    markMoveableRef.current = null;
                  }
                )
              );
            }}
          />
        )}
        {/* マーク削除モーダル */}
        {deleteMarkModal.isOpen && (
          <DeleteConfirmModal
            id="mark-delete-modal"
            message={
              t(Object.values(MarkType).find((mark) => mark.id === selectedMark?.mark_type_id)?.i18n) +
              t("delete_confirmation")
            }
            onClose={() => {
              deleteMarkModal.close();
              setSelectedMark(null);
              selectedMarkRef.current = null;
            }}
            onSubmit={() => {
              dispatch(
                actions.mark.deleteMark(
                  {
                    mark_id: selectedMark.mark_id,
                    timestamp: {
                      update_date: selectedMark.timestamp?.update_date,
                    },
                  },
                  () => {
                    setSelectedMark(null);
                    deleteMarkModal.close();
                    selectedMarkRef.current = null;
                  }
                )
              );
            }}
          />
        )}

        {/* 矢印編集モーダル */}
        {editArrowModal.isOpen && (
          <EditFreeDrawArrowModal
            freeDrawArrow={selectedArrow.freeDrawArrow}
            companyId={drawingArrow.isDrawing ? drawingArrow.companyId : selectedArrow.freeDrawArrow?.company_id}
            color={drawingArrow.isDrawing ? drawingArrow.color : selectedArrow.freeDrawArrow?.color}
            thickness={drawingArrow.isDrawing ? drawingArrow.thickness : selectedArrow.freeDrawArrow?.thickness}
            useStartHour={
              drawingArrow.isDrawing ? drawingArrow.useStartHour : selectedArrow.freeDrawArrow?.use_start_hour
            }
            useEndHour={drawingArrow.isDrawing ? drawingArrow.useEndHour : selectedArrow.freeDrawArrow?.use_end_hour}
            onSaveDrawingArrow={(arrow) => {
              drawingArrow.update(arrow);
              editArrowModal.close();
            }}
            onSave={(arrow: FreeDrawArrow) => {
              selectedArrow.changeColorAndThickness(arrow);
              editArrowModal.close();
            }}
            onClose={() => editArrowModal.close()}
          />
        )}

        {/* 矢印複製モーダル*/}
        {copyArrowModal.isOpen && (
          <CopyObjectModal
            onClose={copyArrowModal.close}
            onSubmit={(dateRange: { layoutDateFrom: string; layoutDateTo: string }) => {
              dispatch(
                actions.arrows.copyArrow({
                  input: {
                    arrow_id: selectedArrow.freeDrawArrow.arrow_id,
                    layout_date_from: dateRange.layoutDateFrom,
                    layout_date_to: dateRange.layoutDateTo,
                  },
                  onSuccess: () => selectedArrow.end(),
                })
              );
            }}
          />
        )}

        {/* 矢印削除モーダル */}
        {deleteArrowModal.isOpen && (
          <DeleteConfirmModal
            id="arrow-delete-modal"
            message={t("arrow") + t("delete_confirmation")}
            onClose={deleteArrowModal.close}
            onSubmit={() => {
              dispatch(
                actions.arrows.deleteArrow({
                  input: {
                    arrow_id: selectedArrow.freeDrawArrow.arrow_id,
                    timestamp: {
                      update_date: selectedArrow.freeDrawArrow.timestamp?.update_date,
                    },
                  },
                  onSuccess: () => {
                    selectedArrow.end();
                    deleteArrowModal.close();
                  },
                })
              );
            }}
          />
        )}

        {/* テキスト編集モーダル */}
        {editTextModal.isOpen && (
          <EditFreeDrawTextModal
            companyId={loginUserCompanyId}
            freeDrawText={selectedText.freeDrawText}
            defaultColor={loginUserCompanyIconColor}
            defaultSize={12}
            onClose={editTextModal.close}
            onChange={selectedText.change}
            initialX={initialPosition.machine.initialX}
            initialY={initialPosition.machine.initialY}
          />
        )}

        {/* テキスト複製モーダル*/}
        {copyTextModal.isOpen && (
          <CopyObjectModal
            onClose={copyTextModal.close}
            onSubmit={(dateRange: { layoutDateFrom: string; layoutDateTo: string }) => {
              dispatch(
                actions.texts.copyText({
                  input: {
                    freetext_id: selectedText.freeDrawText.freetext_id,
                    layout_date_from: dateRange.layoutDateFrom,
                    layout_date_to: dateRange.layoutDateTo,
                  },
                  onSuccess: () => selectedText.end(),
                })
              );
            }}
          />
        )}

        {/* テキスト削除モーダル */}
        {deleteTextModal.isOpen && (
          <DeleteConfirmModal
            id="text-delete-modal"
            message={t("text") + t("delete_confirmation")}
            onClose={deleteTextModal.close}
            onSubmit={() => {
              dispatch(
                actions.texts.deleteText({
                  input: {
                    freetext_id: selectedText.freeDrawText.freetext_id,
                    timestamp: {
                      update_date: selectedText.freeDrawText.timestamp?.update_date,
                    },
                  },
                  onSuccess: () => {
                    selectedText.end();
                    deleteTextModal.close();
                  },
                })
              );
            }}
          />
        )}

        {/* ヘルプモーダル */}
        {helpModal.isOpen && <HelpModal onClose={() => helpModal.close()} />}

        {isLoading && <Loading />}
      </div>
    </div>
  );
};
